import { HttpErrorResponse, HttpEvent, HttpHandler, HttpRequest, HttpResponse, } from '@angular/common/http';
import { from, of, throwError } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';
import { WafCaptchaService } from './waf-captcha.service';
import * as i0 from "@angular/core";
import * as i1 from "./waf-captcha.service";
// Outer guard only: forceRefreshToken() has the SDK's own internal timeout, so
// this matters only if its promise never settles at all. Refresh failure is
// non-fatal — the retry still goes out and the server has the final say.
const REFRESH_TIMEOUT_MS = 3000;
export class WafCaptchaInterceptor {
    constructor(captchaService) {
        this.captchaService = captchaService;
        // Shared across concurrent 202s so a burst of challenged requests triggers
        // one SDK refresh, not one per request.
        this.pendingRefresh = null;
    }
    // A 202 challenge means WAF rejected the token we just sent; getToken()
    // alone would return that same cached token on retry, so force a re-mint.
    refreshToken() {
        if (this.pendingRefresh) {
            return this.pendingRefresh;
        }
        this.pendingRefresh = new Promise((resolve) => {
            let settled = false;
            let timer = null;
            // Returns true only for the winning caller so losers don't log misleading failures.
            const settle = () => {
                if (settled)
                    return false;
                settled = true;
                if (timer !== null)
                    clearTimeout(timer);
                resolve();
                return true;
            };
            if (typeof AwsWafIntegration === 'undefined' || typeof AwsWafIntegration.forceRefreshToken !== 'function') {
                settle();
                return;
            }
            timer = setTimeout(() => {
                if (settle()) {
                    console.warn('WAF CAPTCHA token refresh timed out; proceeding with retry');
                }
            }, REFRESH_TIMEOUT_MS);
            try {
                AwsWafIntegration.forceRefreshToken().then(() => settle(), (err) => {
                    if (settle()) {
                        console.warn('WAF CAPTCHA token refresh failed', err);
                    }
                });
            }
            catch (err) {
                if (settle()) {
                    console.warn('WAF CAPTCHA token refresh failed', err);
                }
            }
        });
        this.pendingRefresh.finally(() => {
            this.pendingRefresh = null;
        });
        return this.pendingRefresh;
    }
    intercept(req, next) {
        let retried = false;
        const attempt = () => next.handle(req).pipe(mergeMap((event) => {
            if (!(event instanceof HttpResponse)) {
                return of(event);
            }
            if (this.isWafChallenge(event)) {
                if (retried) {
                    return throwError(() => new HttpErrorResponse({
                        status: event.status,
                        statusText: event.statusText,
                        headers: event.headers,
                        url: event.url ?? undefined,
                    }));
                }
                retried = true;
                return from(this.refreshToken()).pipe(switchMap(() => attempt()));
            }
            return of(event);
        }), catchError((error) => {
            if (!(error instanceof HttpErrorResponse) || !this.isWafCaptcha(error)) {
                return throwError(() => error);
            }
            if (retried) {
                return throwError(() => error);
            }
            retried = true;
            const originalError = error;
            return from(this.captchaService.solveCaptcha()).pipe(switchMap(() => attempt()), catchError((retryError) => {
                if (retryError instanceof HttpErrorResponse && !this.isWafCaptcha(retryError)) {
                    return throwError(() => retryError);
                }
                return throwError(() => originalError);
            }));
        }));
        // Retries reuse the already-intercepted HttpRequest. Interceptors registered
        // after this one (e.g. WafTokenInterceptor) re-run on retry via next.handle().
        // Interceptors registered before this one (e.g. XSRF) do NOT re-run — but
        // the CSRF header they added is already on the req object, so it persists.
        return attempt();
    }
    isWafCaptcha(error) {
        return error.status === 405 && error.headers.get('x-amzn-waf-action') === 'captcha';
    }
    isWafChallenge(response) {
        return response.status === 202 && response.headers.get('x-amzn-waf-action') === 'challenge';
    }
}
WafCaptchaInterceptor.ɵfac = function WafCaptchaInterceptor_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || WafCaptchaInterceptor)(i0.ɵɵinject(i1.WafCaptchaService)); };
WafCaptchaInterceptor.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: WafCaptchaInterceptor, factory: WafCaptchaInterceptor.ɵfac });
